import React, { useState } from 'react';
import { Link, useRouteMatch, Switch, Route, NavLink, Redirect } from 'react-router-dom';
import { Menu, Layout, Space, Result, Button, Tag } from 'antd';
import useDataSource from 'hooks/useDataSource';
import { DataSourceType, Module } from 'shared/prisma/types';
import Loading from 'components/Loading';
import NoMatch from 'containers/NoMatch';
import { generatePath, useHistory } from 'react-router';
import { MODULE_LABEL } from 'shared/constants/settings';
import { SettingOutlined } from '@ant-design/icons';
import useCustomer from 'hooks/useCustomer';
import useUser from 'hooks/useUser';
import { EDIT_DATA_SOURCE, EDIT_LICENSE } from 'shared/constants/permissions';
import DataSourceSelect from 'components/DataSourceSelect';
import routes from './routes';
import FreeTrialSignUpModal from './FreeTrialSignUpModal';

const { Header, Content } = Layout;

const DataSource = () => {
  const history = useHistory();
  const { path: basePath } = useRouteMatch();
  const { path, params } = useRouteMatch('/data-sources/:dataSourceId/:page?');
  const { changeCustomer, isSuperUser, hasPermission } = useUser();
  const { customerId, loading: customerLoading } = useCustomer();
  const { dataSource, loading: dataSourceLoading, hasDataSourcePermission, hasDataSourceAccess } = useDataSource({ extraFields: 'isDemo' });
  const [showSingUpModal, setShowSingUpModal] = useState(false);

  const loading = dataSourceLoading || customerLoading;

  if (loading) return <Loading />;

  if (!dataSource) return <NoMatch />;

  if (!hasDataSourceAccess()) return <Redirect to="/data-sources" />;

  if (!dataSource.isDemo && customerId !== dataSource.customer.id) {
    changeCustomer(dataSource.customer.id);
    return <Loading />;
  }

  const modules = dataSource.type === DataSourceType.Cx
    ? [Module.CxSurveys]
    : [Module.Analytics, Module.Heatmaps, Module.UxSurveys, Module.Accessibility];

  const filteredRoutes = routes.filter(r => !r.permission || r.permission({ hasPermission, hasDataSourcePermission }));

  const isRouteAvailableForDataSource = (route) => {
    if (route.path === 'dashboard' && dataSource.type === DataSourceType.Cx) {
      return false;
    }
    if (route.module) {
      return modules.includes(route.module);
    }
    return true;
  };

  const availableRoutes = filteredRoutes.filter(isRouteAvailableForDataSource);
  const navRoutes = availableRoutes.filter(route => route.nav !== false && (route.label || route.module));
  const defaultPage = navRoutes[0]?.path || availableRoutes.find(route => route.path !== 'settings')?.path || availableRoutes[0]?.path;

  const items = navRoutes.map(route => ({
    key: route.path || 'dashboard',
    label: <Link to={generatePath(path, { ...params, page: route.path || 'dashboard' })}>{route.label || MODULE_LABEL[route.module]}</Link>,
  }));

  const noLicense = !dataSource.activeLicense && !isSuperUser && params.page !== 'settings';

  return (
    <Layout>
      <Header className="app__subnav nav">
        <div className="nav__left">
          <div className="app__subnav-title">
            {dataSource.isDemo ? (
              <Space>
                <Tag color="red-inverse">Demo</Tag>
                <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {dataSource.title}
                </div>
              </Space>
            ) : (
              <DataSourceSelect style={{ width: 200 }} onChange={(id) => history.push(`/data-sources/${id}`)} value={params.dataSourceId} />
            )}
          </div>
          <Menu mode="horizontal" items={items} selectedKeys={[params?.page || defaultPage]} />
        </div>

        <div className="nav__right">
          {dataSource.isDemo && (
            <div style={{ marginRight: 8 }}>
              <Button onClick={() => setShowSingUpModal(true)}>
                Start your free trial!
              </Button>
            </div>
          )}
          {hasDataSourcePermission(EDIT_DATA_SOURCE) && (
            <NavLink to={generatePath(path, { ...params, page: 'settings' })} className="nav__item">
              <Space>
                <SettingOutlined />
                Data source settings
              </Space>
            </NavLink>
          )}
        </div>

      </Header>
      <Content>
        {noLicense ? (
          <Content style={{ justifyContent: 'center', alignItems: 'center' }}>
            <Result
              status="info"
              title="No active license"
              subTitle={`You need to have a license to see data for ${dataSource.title}.`}
              extra={hasDataSourcePermission(EDIT_LICENSE) ? (
                <Link to={`/data-sources/${dataSource.id}/settings/license`}>
                  <Button type="primary">Setup license</Button>
                </Link>
              ) : (
                <>Ask the owner to setup a license.</>
              )}
            />
          </Content>
        ) : (
          <Switch>
            {availableRoutes.map(route => (
              <Route
                key={route.path}
                exact={route.exact}
                path={`${basePath}/${route.path}`}
              >
                <route.component {...route.props} />
              </Route>
            ))}
            <Route path="*">
              <Redirect to={generatePath(path, { ...params, page: defaultPage })} />
            </Route>
          </Switch>
        )}
        <FreeTrialSignUpModal open={showSingUpModal} onCancel={() => setShowSingUpModal(false)} />
      </Content>
    </Layout>

  );
};

export default DataSource;
